import React, { useContext, useEffect, useState } from "react";
import { getClubMemberships } from "../../Api/api";
import moment from "moment";
import "./ClubProfileMembership.css";
import { Store } from "react-notifications-component";
import { AuthContext } from "../AuthProvider";
import { checkNullAndUndefined, formatCurrency } from "../../Utilities/utils";
import { useTranslation } from "react-i18next";
import { Modal } from "../ui/Modal";
import { CgSpinner } from "react-icons/cg";
import { MembershipModal } from "./MembershipModal";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import SeeMoreText from "./SeeMoreText";
import PrivateMembershipCheckout from "./PrivateMembershipCheckout";
import { MembershipPaymentDetailsModal } from "./MembershipPaymentDetailsModal";
import { Moment } from "moment";
import { useNavigate } from "react-router";

interface ClubProfileMembershipProps {
    clubData: any;
    clubId: string;
    currency: string;
    timezone: string;
}

export interface MemberShipDetails {
    Date_of_birth?: Moment | null;
    Birth_Date?: Moment | null;
    Personal_Number?: string;
    Phone_number: string;
    Address: string;
    Postal_no: string;
    County: string;
    Country: string;
}

const ClubProfileMembership: React.FC<ClubProfileMembershipProps> = ({
    clubData,
    clubId,
    currency,
    timezone
}) => {
    const [clubMemberships, setClubMemberships] = useState<any>([]);
    const [membership, setMembership] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [openMembershipModal, setOpenMembershipModal] = useState(false);
    const [openPrivateMembershipModal, setOpenPrivateMembershipModal] =
        useState(false);
    const [openMembershipPaymentDetails, setOpenMembershipPaymentDetails] =
        useState(false);
    const [formData, setFormData] = useState<MemberShipDetails>({
        Personal_Number: "",
        Phone_number: "",
        Address: "",
        Postal_no: "",
        County: "",
        Country: ""
    });
    const NO_BINDING_PERIOD = "NO BINDING PERIOD";
    const currentUser = useContext(AuthContext);
    const { t } = useTranslation();
    const navigate = useNavigate();

    const publishableKey = process.env.REACT_APP_STRIPE_KEY as string;
    const stripePromise = loadStripe(publishableKey);

    useEffect(() => {
        let mounted = true;

        const fetchMemberships = async () => {
            if (mounted && currentUser && clubId) {
                setLoading(true);
                try {
                    const membershipResp = await getClubMemberships(clubId);
                    if (
                        membershipResp &&
                        membershipResp?.data?.memberships &&
                        membershipResp?.data?.memberships?.length
                    ) {
                        const filterData =
                            membershipResp?.data?.memberships?.filter(
                                (membership: any) => membership.hidden === false
                            );
                        setClubMemberships(filterData || []);
                    }
                } catch (error) {
                } finally {
                    if (mounted) {
                        setLoading(false);
                    }
                }
            }
        };

        fetchMemberships();

        return () => {
            mounted = false;
        };
    }, [clubId, currentUser]);

    const handleMembershipBtnPress = (membership: any) => {
        if (clubData?.membership_status && !clubData?.multi_membership) {
            Store.addNotification({
                title: "You are already a member!",
                message:
                    "You can only have one membership at a time, please cancel your membership!",
                type: "danger",
                container: "bottom-center",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: {
                    duration: 3000
                }
            });

            return;
        }

        setMembership(membership);

        if (
            membership.type === "Private" &&
            membership.sub_type === "ONE_TIME_PASS"
        ) {
            setOpenPrivateMembershipModal(true);
        } else {
            if (clubData?.currency === "SEK") {
                setFormData({
                    Personal_Number: "",
                    Phone_number: "",
                    Address: "",
                    Postal_no: "",
                    County: "",
                    Country: ""
                });
            } else {
                setFormData({
                    Date_of_birth: null,
                    Phone_number: "",
                    Address: "",
                    Postal_no: "",
                    County: "",
                    Country: ""
                });
            }
            setOpenMembershipModal(true);
        }
    };

    return (
        <div className="text-[15px]">
            {!currentUser ? (
                <button className="hover:underline" onClick={()=> navigate("/sign-in")}>{t("loginToCheckMemberships")}</button>
            ) : (
                <>
                    {loading ? (
                        <div className="flex justify-center items-center mt-10">
                            <CgSpinner className="animate-spin text-[24px]" />
                        </div>
                    ) : (
                        <div
                            className={`${
                                clubMemberships.length ? "mt-7" : "mt-3"
                            } pb-4 md:pb-0 flex flex-row md:flex-col gap-6 overflow-y-auto`}
                        >
                            {clubMemberships.length ? (
                                clubMemberships?.map(
                                    (membership: any, index: number) => {
                                        const bindingPeriod = membership?.binding_period
                                            ? membership?.binding_period === NO_BINDING_PERIOD
                                                ? membership?.binding_period
                                                : `${t("bindingPeriod")}: ${membership?.binding_period} ${
                                                        +membership?.binding_period > 1
                                                            ? t("months")
                                                            : t("month")
                                                    }`
                                            : NO_BINDING_PERIOD;
                                        return (
                                        <div
                                            key={index}
                                            className="rounded-[10px] border-2 border-secondary border-opacity-[0.15] px-2 py-5 sm:px-5 text-center min-w-[250px] flex flex-col justify-between max-h-[450px] md:max-h-full overflow-y-auto"
                                        >
                                            <div>
                                                <h3 className="text-xl font-semibold break-words">
                                                    {" "}
                                                    {membership.name}{" "}
                                                </h3>
                                                {membership.type !==
                                                "Public" ? (
                                                    <p className="text-sm mt-1 break-words">
                                                        {t("period")}:{" "}
                                                        {moment
                                                            .utc(
                                                                membership.start_date
                                                            )
                                                            .tz(timezone)
                                                            .format(
                                                                "DD MMM"
                                                            )}{" "}
                                                        -
                                                        {moment
                                                            .utc(
                                                                membership.end_date
                                                            )
                                                            .tz(timezone)
                                                            .format(
                                                                "DD MMM YYYY"
                                                            )}
                                                    </p>
                                                ) : null}
                                                {membership.type === "Public" &&
                                                    checkNullAndUndefined(
                                                        membership.player_end_datetime
                                                    ) &&
                                                    membership.period ===
                                                        "Monthly" && (
                                                        <p className="text-sm mt-1 break-words">
                                                            {membership.binding_period
                                                                ? bindingPeriod
                                                                : t(
                                                                      "noBindingPeriod"
                                                                  )}
                                                        </p>
                                                    )}
                                                {membership.information && (
                                                    <div className="mt-4">
                                                        {membership.information ? (
                                                            <SeeMoreText
                                                                numberOfWords={
                                                                    80
                                                                }
                                                                text={
                                                                    membership.information
                                                                }
                                                            />
                                                        ) : null}
                                                    </div>
                                                )}
                                                <div className="max-w-fit mx-auto mt-4 flex flex-col gap-1 items-center justify-center">
                                                    {membership.usp_1 ? (
                                                        <p className="text-[15px] flex gap-2 capitalize break-words">
                                                            <img
                                                                src="/images/check-mark.svg"
                                                                alt="check"
                                                            />
                                                            <span>
                                                                {" "}
                                                                {
                                                                    membership.usp_1
                                                                }{" "}
                                                            </span>
                                                        </p>
                                                    ) : (
                                                        <></>
                                                    )}
                                                    {membership.usp_2 ? (
                                                        <p className="text-[15px] flex gap-2 capitalize break-words">
                                                            <img
                                                                src="/images/check-mark.svg"
                                                                alt="check"
                                                            />
                                                            <span>
                                                                {" "}
                                                                {
                                                                    membership.usp_2
                                                                }{" "}
                                                            </span>
                                                        </p>
                                                    ) : (
                                                        <></>
                                                    )}
                                                    {membership.usp_3 ? (
                                                        <p className="text-[15px] flex gap-2 capitalize break-words">
                                                            <img
                                                                src="/images/check-mark.svg"
                                                                alt="check"
                                                            />
                                                            <span>
                                                                {" "}
                                                                {
                                                                    membership.usp_3
                                                                }{" "}
                                                            </span>
                                                        </p>
                                                    ) : (
                                                        <></>
                                                    )}
                                                    {membership.usp_4 ? (
                                                        <p className="text-[15px] flex gap-2 capitalize break-words">
                                                            <img
                                                                src="/images/check-mark.svg"
                                                                alt="check"
                                                            />
                                                            <span>
                                                                {" "}
                                                                {
                                                                    membership.usp_4
                                                                }{" "}
                                                            </span>
                                                        </p>
                                                    ) : (
                                                        <></>
                                                    )}
                                                </div>
                                                {membership?.type ===
                                                "Public" ? (
                                                    <p className="text-base font-semibold mt-4 break-words">
                                                        {`${formatCurrency(membership?.price, currency)}/${membership?.period
                                                            ?.split("ly")[0]
                                                            ?.toLowerCase()}`}
                                                    </p>
                                                ) : null}
                                            </div>
                                            <button
                                                className={
                                                    !membership.is_member
                                                        ? `w-full min-h-[49px] text-white bg-secondary text-base font-semibold rounded-full mt-4 cursor-pointer`
                                                        : `w-full min-h-[49px] text-secondary bg-primary text-base font-bold rounded-full mt-4`
                                                }
                                                disabled={membership.is_member}
                                                onClick={() => {
                                                    handleMembershipBtnPress(
                                                        membership
                                                    );
                                                }}
                                            >
                                                {membership.is_member
                                                    ? t("alreadyMember")
                                                    : t("becomeMember")}
                                            </button>
                                        </div>
                                    )}
                                )
                            ) : (
                                <p className="text-[15px]">
                                    {t("noMembershipsAvailable")}
                                </p>
                            )}
                        </div>
                    )}
                </>
            )}
            <Elements stripe={stripePromise}>
                <Modal
                    open={openPrivateMembershipModal}
                    setOpen={setOpenPrivateMembershipModal}
                >
                    <PrivateMembershipCheckout
                        membership={membership}
                        clubData={clubData}
                        setOpenPrivateMembershipModal={
                            setOpenPrivateMembershipModal
                        }
                        setClubMemberships={setClubMemberships}
                        isLoading={loading}
                        setLoading={setLoading}
                    />
                </Modal>
                <Modal
                    open={openMembershipModal}
                    setOpen={setOpenMembershipModal}
                >
                    <MembershipModal
                        clubData={clubData}
                        setOpenMembershipModal={setOpenMembershipModal}
                        setOpenMembershipPaymentDetails={
                            setOpenMembershipPaymentDetails
                        }
                        formData={formData}
                        setFormData={setFormData}
                    />
                </Modal>
                <Modal
                    open={openMembershipPaymentDetails}
                    setOpen={setOpenMembershipPaymentDetails}
                >
                    <MembershipPaymentDetailsModal
                        membership={membership}
                        clubData={clubData}
                        formData={formData}
                        setOpenMembershipPaymentDetails={
                            setOpenMembershipPaymentDetails
                        }
                        setOpenMembershipModal={setOpenMembershipModal}
                        setClubMemberships={setClubMemberships}
                        isLoading={loading}
                        setLoading={setLoading}
                    />
                </Modal>
            </Elements>
        </div>
    );
};

export default ClubProfileMembership;